import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { InputAdornment, OutlinedInput, Stack } from '@mui/material';
import React, { useState } from 'react';
import { Button } from './button';
import { IconButton } from './iconButton';

interface SecretValueProps {
  readonly value: string;
  // Whether to render the "Copy" button. Defaults to true. Copying works
  // regardless of reveal state, so a value can be shared without ever being
  // shown on screen.
  readonly copy?: boolean;
}

// Read-only display for a sensitive value (e.g. a temporary password). Renders
// a native `type="password"` field so the value is masked by default — an admin
// has to click the eye icon to reveal it. Styled as a filled, borderless box to
// match the surrounding credential fields. Use this anywhere a secret would
// otherwise be printed in plain text.
export function SecretValue({ value, copy = true }: SecretValueProps) {
  const [revealed, setRevealed] = useState<boolean>(false);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <OutlinedInput
        readOnly
        fullWidth
        type={revealed ? 'text' : 'password'}
        value={value}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: '1.05rem' } }}
        sx={{
          backgroundColor: 'action.hover',
          borderRadius: 1,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              variant="ghost"
              size="s"
              aria-label={revealed ? 'Hide value' : 'Reveal value'}
              onClick={() => setRevealed(previous => !previous)}
            >
              {revealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </InputAdornment>
        }
      />
      {copy && (
        <Button
          variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
          }}
        >
          Copy
        </Button>
      )}
    </Stack>
  );
}
